import React, { useState } from "react";
import { Mail } from "lucide-react";

import {
    MainLayout,
    SearchInput,
    ListToolbar,
    Pagination,
    LoadingState,
    EmptyState,
    ErrorBanner,
    useDebouncedValue,
    useIsMobile,
} from "../ui/components";
import { usePaginatedList } from "../../api/usePaginatedList";
import { UserProfile, UserMessage } from "../../types/objects";

const formatDate = (iso: string) =>
    new Date(iso).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * Messages module — messages sent through the public contact form (`usermessages`),
 * newest first, with case-insensitive search over sender and content.
 */
export default function Messages({ user: _user }: { user: UserProfile }) {
    const [searchTerm, setSearchTerm] = useState("");
    const search = useDebouncedValue(searchTerm);
    const isMobile = useIsMobile();
    const list = usePaginatedList<UserMessage>("usermessages", { search });

    const body = () => {
        if (list.loading && list.rows.length === 0) return <LoadingState />;
        if (list.rows.length === 0) {
            return <EmptyState label={search ? "Hledání neodpovídá žádná zpráva." : "Zatím nebyly přijaty žádné zprávy."} />;
        }
        if (isMobile) {
            return (
                <div className="space-y-4">
                    {list.rows.map((msg) => (
                        <div key={msg.documentId} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm space-y-3">
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0"><Mail size={18} /></div>
                                    <span className="font-black text-slate-900 truncate">{msg.sender || "Anonymní odesílatel"}</span>
                                </div>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest shrink-0">{formatDate(msg.createdAt)}</span>
                            </div>
                            <p className="text-slate-600 font-medium leading-relaxed text-sm whitespace-pre-line">{msg.content}</p>
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-200">
                            <th className="px-8 py-5 font-black text-slate-400 text-[10px] uppercase tracking-widest">Odesílatel</th>
                            <th className="px-8 py-5 font-black text-slate-400 text-[10px] uppercase tracking-widest">Obsah zprávy</th>
                            <th className="px-8 py-5 font-black text-slate-400 text-[10px] uppercase tracking-widest text-right">Přijato</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {list.rows.map((msg) => (
                            <tr key={msg.documentId} className="hover:bg-slate-50/30 transition-colors align-top">
                                <td className="px-8 py-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Mail size={18} /></div>
                                        <span className="text-base font-bold text-slate-900">{msg.sender || "Anonymní odesílatel"}</span>
                                    </div>
                                </td>
                                <td className="px-8 py-6 text-slate-600 font-medium leading-relaxed max-w-xl whitespace-pre-line">{msg.content}</td>
                                <td className="px-8 py-6 text-right font-bold text-slate-400 text-xs whitespace-nowrap">{formatDate(msg.createdAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <MainLayout title="Zprávy" subtitle="Zprávy odeslané návštěvníky webu přes kontaktní formulář.">
            <ListToolbar
                total={list.pagination.total}
                pageSize={list.pageSize}
                loading={list.loading}
                onRefresh={list.reload}
                onPageSizeChange={list.setPageSize}
            >
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Hledat odesílatele nebo obsah…" />
            </ListToolbar>

            <div className="space-y-6">
                {list.error && <ErrorBanner message={list.error} onRetry={list.reload} />}
                {body()}
                <Pagination page={list.page} pageCount={list.pagination.pageCount} total={list.pagination.total} onPageChange={list.setPage} />
            </div>
        </MainLayout>
    );
}
